<template>
  <div class="kanban-pipelines">
    <EditableBoard
      :value="value"
      :columns="columns"
      :add-fields="true"
      :create-label="$t('add')"
      :custom-add="add"
      :sortable="false"
    >
      <template #label="{ item }">
        <span data-part="label">{{ item.label }}</span>
        <span data-part="columns">
          <template v-for="(column, index) in item.columns">
            <span :style="`--c: ${column.color};`">{{ column.label }}</span>
          </template>
        </span>
      </template>

      <template #actions="{ item }">
        <DropdownComponent :close-on-click="false">
          <template #default>
            <ButtonComponent icon="fa-pro-light:ellipsis-vertical" />
          </template>
          <template #dropdown="{ close }">
            <ButtonComponent :label="$t('clone')" icon="fa-pro-light:copy" @click="() => duplicate(item, close)" />
            <ButtonComponent :label="$t('edit')" icon="fa-pro-light:pen" @click="() => edit(item, close)" />
            <ButtonComponent
              :label="$t('remove')"
              icon="fa-pro-light:trash"
              confirm
              @click="() => remove(item, close)"
            />
          </template>
        </DropdownComponent>
      </template>
    </EditableBoard>

    <Portal>
      <ModalComponent
        v-if="Boolean(current)"
        :show="Boolean(current)"
        teleport
        @close="cancel"
        class="kanban-settings-modal-wrapper"
      >
        <Pipeline :pipeline="current" @save="save" @cancel="cancel" />
      </ModalComponent>
    </Portal>
  </div>
</template>

<script>
import EditableBoard from '@/components/EditableBoard.vue'
import { ModalComponent } from 'vue-elder-modal'
import Pipeline from './Pipeline.vue'
import { DropdownComponent } from 'vue-elder-dropdown'
import Portal from '@/components/Portal'

const PipelineTemplate = {
  label: '',
  condition: null,
  columns: [],
}

export default {
  props: {
    value: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      current: null,
    }
  },
  computed: {
    columns() {
      return [
        { id: 'label', label: this.$t('name'), size: '2fr' },
        { id: 'actions', label: '', size: '80px', align: 'end' },
      ]
    },
  },
  methods: {
    add() {
      this.current = structuredClone(PipelineTemplate)
    },
    edit(item, close) {
      this.current = item
      close()
    },
    cancel() {
      this.current = null
    },
    save(item) {
      const index = this.value.findIndex(i => i === this.current)
      if (index === -1) this.$emit('input', [...this.value, item])
      else this.$emit('input', this.value.with(index, item))

      this.cancel()
    },
    remove(item, close) {
      this.$emit(
        'input',
        this.value.filter(i => i !== item),
      )
      close()
    },
    duplicate(pipeline, close) {
      this.$emit('input', [
        ...this.value,
        { ...structuredClone(pipeline), label: `${this.$t('copyOf')} ${pipeline.label}` },
      ])
      close()
    },
  },
  components: {
    EditableBoard,
    ModalComponent,
    Pipeline,
    DropdownComponent,
    Portal,
  },
}
</script>

<style lang="scss">
.kanban-pipelines {
  [data-cell-id='actions'] {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;

    font-size: 0.8em;
  }

  [data-cell-id='label'] {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;

    [data-part='columns'] {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.25rem 0.25rem;

      font-size: 0.7em;

      white-space: nowrap;

      span {
        display: flex;
        align-items: center;
        gap: 2px;

        &:before {
          content: '';
          display: inline-block;
          width: 10px;
          height: 10px;
          border-radius: 3px;
          background-color: var(--c);
        }

        &:not(:last-child):after {
          margin-left: 3px;
          content: '/';
          opacity: 0.5;
        }
      }

      svg {
        font-size: 0.8em;
      }
    }
  }
}

.kanban-settings-modal-wrapper {
  ::v-deep .modal {
    width: 100%;
    max-width: 700px;
  }
}
</style>
